import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Chart } from "primereact/chart";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { MultiSelect } from "primereact/multiselect";
import { OverlayPanel } from "primereact/overlaypanel";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { useRef } from "react";
import { WeekPicker } from "../components/WeekPicker";
import { ActivityBadge, ReferenceBadge } from "../components/Badges";
import { getCurrentWeek, formatWeek, parseWeekKey } from "../utils/weekUtils";
import { useApiAuth } from "../hooks/useApiAuth";
import { apiService } from "../services/apiService";

// Types based on the API responses
interface Activity {
  hour_type: "billable" | "non_billable";
  id: number;
  name: string;
  project_id: number;
  schedulable: boolean;
  service_type_id: number | null;
}

interface Person {
  email: string;
  id: number;
  name: string;
  plannable: boolean;
  surname: string;
}

interface ProjectInfo {
  activities: Activity[];
  client: {
    external: boolean;
    id: number;
    name: string;
  };
  fee_hourly: string;
  fee_total: string;
  hour_type: "billable" | "non_billable";
  id: number;
  manager: Person;
  name: string;
  reference: string;
  reservation: boolean;
  status: {
    completed: boolean;
    id: number;
    name: string;
    win: boolean;
  };
}

interface ChartData {
  data: {
    series1: {
      billable: { data: number[]; name: string };
      non_billable: { data: number[]; name: string };
    };
    series2: {
      billable: { data: (number | string)[]; name: string };
      non_billable: { data: (number | string)[]; name: string };
    };
  };
  labels: number[];
  table: Array<{
    activity: number;
    actuals: number;
    name: string;
    planned: number;
  }>;
}

interface WeekRange {
  week_from: number;
  week_to: number;
}

interface TableRow {
  id: string; // Unique identifier for each row
  activity?: number;
  actuals: number;
  name: string;
  planned: number;
  isPersonRow?: boolean;
  parentActivity?: number;
  isExpanded?: boolean;
}

export const ProjectPage: React.FC = () => {
  const { reference } = useParams<{ reference: string }>();
  const { isApiReady } = useApiAuth();
  const toast = useRef<Toast>(null);
  const filterPanelRef = useRef<OverlayPanel>(null);

  // State
  const [projectInfo, setProjectInfo] = useState<ProjectInfo | null>(null);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);

  // Filters
  const [selectedActivities, setSelectedActivities] = useState<number[]>([]);
  const [selectedRange, setSelectedRange] = useState<string>("ytd");
  const [customWeekFrom, setCustomWeekFrom] = useState<number>(1);
  const [customWeekTo, setCustomWeekTo] = useState<number>(getCurrentWeek());
  const [customYearFrom, setCustomYearFrom] = useState<number>(
    new Date().getFullYear(),
  );
  const [customYearTo, setCustomYearTo] = useState<number>(
    new Date().getFullYear(),
  );

  // Expanded rows for table
  const [expandedRows, setExpandedRows] = useState<any>({});

  // Track theme state for chart re-rendering
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return (
      document.documentElement.classList.contains("p-dark") ||
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  });
  const [themeKey, setThemeKey] = useState(0);

  // Listen for global theme changes (no polling)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { isDarkMode?: boolean }
        | undefined;
      const next =
        typeof detail?.isDarkMode === "boolean"
          ? detail.isDarkMode
          : document.documentElement.classList.contains("p-dark");
      setIsDarkMode((prev) => {
        if (prev !== next) {
          setThemeKey((k) => k + 1);
        }
        return next;
      });
    };
    window.addEventListener("themechange", handler as EventListener);

    // Fire once on mount to sync initial state
    handler(
      new CustomEvent("themechange", {
        detail: {
          isDarkMode: document.documentElement.classList.contains("p-dark"),
        },
      }),
    );

    return () =>
      window.removeEventListener("themechange", handler as EventListener);
  }, []);

  // Calculate week range based on selected preset
  const getWeekRange = useCallback((): WeekRange | {} => {
    const currentWeek = getCurrentWeek();
    const currentYear = new Date().getFullYear();

    switch (selectedRange) {
      case "ytd":
        return {
          week_from: formatWeek(currentYear, 1),
          week_to: formatWeek(currentYear, currentWeek),
        };
      case "26weeks":
        const from26 = Math.max(1, currentWeek - 25);
        return {
          week_from: formatWeek(currentYear, from26),
          week_to: formatWeek(currentYear, currentWeek),
        };
      case "13weeks":
        const from13 = Math.max(1, currentWeek - 12);
        return {
          week_from: formatWeek(currentYear, from13),
          week_to: formatWeek(currentYear, currentWeek),
        };
      case "thisyear":
        return {
          week_from: formatWeek(currentYear, 1),
          week_to: formatWeek(currentYear, 52),
        };
      case "custom":
        return {
          week_from: formatWeek(customYearFrom, customWeekFrom),
          week_to: formatWeek(customYearTo, customWeekTo),
        };
      case "all":
      default:
        return {};
    }
  }, [
    selectedRange,
    customWeekFrom,
    customWeekTo,
    customYearFrom,
    customYearTo,
  ]);

  // Load chart data
  const loadChartData = useCallback(async () => {
    if (!reference || selectedActivities.length === 0 || !isApiReady) return;

    setChartLoading(true);
    try {
      const range = getWeekRange();
      const body = {
        activities: selectedActivities,
        range,
      };

      const response = await apiService.post<ChartData>(
        `/project/barchart/${reference}`,
        body,
      );
      if (response.success) {
        setChartData(response.data);
      } else {
        throw new Error(response.message || "Failed to load chart data");
      }
    } catch (error) {
      console.error("Failed to load chart data:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load chart data",
      });
    } finally {
      setChartLoading(false);
    }
  }, [reference, selectedActivities, isApiReady, getWeekRange]);

  // Apply quick range presets
  const applyRangePreset = useCallback((preset: string) => {
    const currentWeek = getCurrentWeek();
    const currentYear = new Date().getFullYear();

    switch (preset) {
      case "ytd":
        setCustomWeekFrom(1);
        setCustomWeekTo(currentWeek);
        setCustomYearFrom(currentYear);
        setCustomYearTo(currentYear);
        setSelectedRange("custom");
        break;
      case "26weeks":
        const from26 = Math.max(1, currentWeek - 25);
        setCustomWeekFrom(from26);
        setCustomWeekTo(currentWeek);
        setCustomYearFrom(currentYear);
        setCustomYearTo(currentYear);
        setSelectedRange("custom");
        break;
      case "13weeks":
        const from13 = Math.max(1, currentWeek - 12);
        setCustomWeekFrom(from13);
        setCustomWeekTo(currentWeek);
        setCustomYearFrom(currentYear);
        setCustomYearTo(currentYear);
        setSelectedRange("custom");
        break;
      case "thisyear":
        setCustomWeekFrom(1);
        setCustomWeekTo(52);
        setCustomYearFrom(currentYear);
        setCustomYearTo(currentYear);
        setSelectedRange("custom");
        break;
      case "all":
        setSelectedRange("all");
        break;
    }
  }, []);

  // Apply filters and close panel
  const applyFilters = useCallback(() => {
    filterPanelRef.current?.hide();
    loadChartData();
  }, [loadChartData]);

  // Load project info
  useEffect(() => {
    if (!reference || !isApiReady) return;

    const loadProjectInfo = async () => {
      try {
        const response = await apiService.get<ProjectInfo>(
          `/projects/${reference}`,
        );
        if (response.success) {
          setProjectInfo(response.data);

          // Initialize selected activities with all activities
          if (response.data.activities) {
            setSelectedActivities(
              response.data.activities.map((a: Activity) => a.id),
            );
          }
        } else {
          throw new Error(
            response.message || "Failed to load project information",
          );
        }
      } catch (error) {
        console.error("Failed to load project info:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load project information",
        });
      } finally {
        setLoading(false);
      }
    };

    loadProjectInfo();
  }, [reference, isApiReady]);

  // Reload chart data when filters change
  useEffect(() => {
    if (projectInfo && selectedActivities.length > 0) {
      loadChartData();
    }
  }, [projectInfo, selectedActivities, loadChartData]);

  // Prepare chart configuration
  const chartConfig = useMemo(() => {
    if (!chartData) return null;

    const weekLabels = chartData.labels.map((label) => {
      const { weekNumber } = parseWeekKey(label.toString());
      return `W${weekNumber}`;
    });

    // Get computed colors for dark mode compatibility
    const colors = {
      plannedBillable: {
        bg: isDarkMode ? "#60A5FA" : "#3B82F6", // blue-400 : blue-500
        border: isDarkMode ? "#3B82F6" : "#1D4ED8", // blue-500 : blue-700
      },
      plannedNonBillable: {
        bg: isDarkMode ? "#4ADE80" : "#10B981", // green-400 : emerald-500
        border: isDarkMode ? "#10B981" : "#059669", // emerald-500 : emerald-600
      },
      actualBillable: {
        bg: isDarkMode ? "#F472B6" : "#EC4899", // pink-400 : pink-500
        border: isDarkMode ? "#EC4899" : "#BE185D", // pink-500 : pink-700
      },
      actualNonBillable: {
        bg: isDarkMode ? "#A78BFA" : "#8B5CF6", // violet-400 : violet-500
        border: isDarkMode ? "#8B5CF6" : "#7C3AED", // violet-500 : violet-600
      },
      plannedNonWorking: {
        bg: isDarkMode ? "#FDE047" : "#EAB308", // yellow-300 : yellow-500
        border: isDarkMode ? "#EAB308" : "#A16207", // yellow-500 : yellow-700
      },
      actualNonWorking: {
        bg: isDarkMode ? "#FB923C" : "#F97316", // orange-400 : orange-500
        border: isDarkMode ? "#F97316" : "#C2410C", // orange-500 : orange-700
      },
    };

    const datasets = [];
    const zeroData = new Array(chartData.labels.length).fill(0);

    // Helper function to check if any data exists in an array
    const hasData = (data?: number[] | (number | string)[]): boolean => {
      if (!data) return false;
      return data.some((val) => {
        const num = typeof val === "string" ? parseFloat(val) : val;
        return !isNaN(num) && num !== 0;
      });
    };

    // Get data references
    const plannedBillable = chartData.data.series1?.billable;
    const actualBillable = chartData.data.series2?.billable;
    const plannedNonBillable = chartData.data.series1?.non_billable;
    const actualNonBillable = chartData.data.series2?.non_billable;
    const plannedNonWorking = (chartData.data.series1 as any)?.non_working;
    const actualNonWorking = (chartData.data.series2 as any)?.non_working;

    // Check if billable data exists (show both planned and actual if either has data)
    const hasBillableData =
      hasData(plannedBillable?.data) || hasData(actualBillable?.data);
    if (hasBillableData) {
      // Planned billable
      datasets.push({
        label: plannedBillable?.name || "Planned Billable",
        data: plannedBillable?.data || zeroData,
        backgroundColor: colors.plannedBillable.bg,
        borderColor: colors.plannedBillable.border,
        borderWidth: 1,
        stack: "planned",
      });

      // Actual billable
      datasets.push({
        label: actualBillable?.name || "Actual Billable",
        data:
          actualBillable?.data?.map((v) =>
            typeof v === "string" ? parseFloat(v) : v,
          ) || zeroData,
        backgroundColor: colors.actualBillable.bg,
        borderColor: colors.actualBillable.border,
        borderWidth: 1,
        stack: "actual",
      });
    }

    // Check if non-billable data exists (show both planned and actual if either has data)
    const hasNonBillableData =
      hasData(plannedNonBillable?.data) || hasData(actualNonBillable?.data);
    if (hasNonBillableData) {
      // Planned non-billable
      datasets.push({
        label: plannedNonBillable?.name || "Planned Non-Billable",
        data: plannedNonBillable?.data || zeroData,
        backgroundColor: colors.plannedNonBillable.bg,
        borderColor: colors.plannedNonBillable.border,
        borderWidth: 1,
        stack: "planned",
      });

      // Actual non-billable
      datasets.push({
        label: actualNonBillable?.name || "Actual Non-Billable",
        data:
          actualNonBillable?.data?.map((v) =>
            typeof v === "string" ? parseFloat(v) : v,
          ) || zeroData,
        backgroundColor: colors.actualNonBillable.bg,
        borderColor: colors.actualNonBillable.border,
        borderWidth: 1,
        stack: "actual",
      });
    }

    // Check if non-working data exists (show both planned and actual if either has data)
    const hasNonWorkingData =
      hasData(plannedNonWorking?.data) || hasData(actualNonWorking?.data);
    if (hasNonWorkingData) {
      // Planned non-working
      datasets.push({
        label: plannedNonWorking?.name || "Planned Non-Working",
        data: plannedNonWorking?.data || zeroData,
        backgroundColor: colors.plannedNonWorking.bg,
        borderColor: colors.plannedNonWorking.border,
        borderWidth: 1,
        stack: "planned",
      });

      // Actual non-working
      datasets.push({
        label: actualNonWorking?.name || "Actual Non-Working",
        data:
          actualNonWorking?.data?.map((v: any) =>
            typeof v === "string" ? parseFloat(v) : v,
          ) || zeroData,
        backgroundColor: colors.actualNonWorking.bg,
        borderColor: colors.actualNonWorking.border,
        borderWidth: 1,
        stack: "actual",
      });
    }

    return {
      labels: weekLabels,
      datasets,
    };
  }, [chartData, isDarkMode]);

  const chartOptions = useMemo(() => {
    // Get computed colors for dark mode compatibility
    const textColor = isDarkMode ? "#E5E7EB" : "#374151"; // gray-200 : gray-700
    const secondaryTextColor = isDarkMode ? "#9CA3AF" : "#6B7280"; // gray-400 : gray-500
    const borderColor = isDarkMode ? "#374151" : "#E5E7EB"; // gray-700 : gray-200

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top" as const,
          labels: {
            color: textColor,
          },
        },
        title: {
          display: false,
          text: "Project Hours: Planned vs Actual",
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: "Weeks",
            color: textColor,
          },
          ticks: {
            color: secondaryTextColor,
          },
          grid: {
            color: borderColor,
          },
        },
        y: {
          title: {
            display: true,
            text: "Hours",
            color: textColor,
          },
          beginAtZero: true,
          ticks: {
            color: secondaryTextColor,
          },
          grid: {
            color: borderColor,
          },
        },
      },
    };
  }, [isDarkMode]);

  // Prepare table data with expandable rows
  const tableData = useMemo(() => {
    if (!chartData) return [];

    const rows: TableRow[] = [];

    // Group table data by activity
    const activityMap = new Map<
      number,
      {
        planned: number;
        actual: number;
        people: Array<{ name: string; planned: number; actual: number }>;
      }
    >();

    chartData.table.forEach((row) => {
      if (!activityMap.has(row.activity)) {
        activityMap.set(row.activity, { planned: 0, actual: 0, people: [] });
      }
      const activityData = activityMap.get(row.activity)!;
      activityData.planned += row.planned;
      activityData.actual += row.actuals;
      activityData.people.push({
        name: row.name,
        planned: row.planned,
        actual: row.actuals,
      });
    });

    // Create rows for activities and people - sorted by activity name
    const sortedActivities = Array.from(activityMap.entries()).sort(
      ([idA, dataA], [idB, dataB]) => {
        const activityA = projectInfo?.activities.find((a) => a.id === idA);
        const activityB = projectInfo?.activities.find((a) => a.id === idB);
        const nameA = activityA ? activityA.name : `Activity ${idA}`;
        const nameB = activityB ? activityB.name : `Activity ${idB}`;
        return nameA.localeCompare(nameB);
      },
    );

    sortedActivities.forEach(([activityId, data]) => {
      const activity = projectInfo?.activities.find((a) => a.id === activityId);
      const activityName = activity ? activity.name : `Activity ${activityId}`;

      // Activity row
      rows.push({
        id: `activity_${activityId}`,
        activity: activityId,
        name: activityName,
        planned: data.planned,
        actuals: data.actual,
        isPersonRow: false,
        isExpanded: expandedRows[activityId] === true,
      });

      // Person rows (will be shown when expanded) - sorted by name
      const sortedPeople = data.people.sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      sortedPeople.forEach((person, personIndex) => {
        rows.push({
          id: `person_${activityId}_${personIndex}`,
          name: person.name,
          planned: person.planned,
          actuals: person.actual,
          isPersonRow: true,
          parentActivity: activityId,
        });
      });
    });

    return rows;
  }, [chartData, projectInfo, expandedRows]);

  // Filter table data based on expansion state
  const visibleTableData = useMemo(() => {
    return tableData.filter((row) => {
      if (!row.isPersonRow) return true; // Always show activity rows
      const parentActivity = row.parentActivity;
      if (parentActivity === undefined) return false;
      const isParentExpanded = expandedRows[parentActivity] === true;
      return isParentExpanded; // Show person rows only if parent is expanded
    });
  }, [tableData, expandedRows]);

  if (loading) {
    return (
      <div className="page-container">
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <ProgressSpinner />
        </div>
      </div>
    );
  }

  if (!projectInfo) {
    return (
      <div className="page-container">
        <Card title="Project Not Found" className="mb-4">
          <p>Project with reference "{reference}" could not be found.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-container">
      <Toast ref={toast} />

      {/* Page Header with Title and Filter Button */}
      <div className="flex justify-content-between align-items-center mb-4">
        <h1 className="text-3xl font-bold m-0">
          {projectInfo.client.name} - {projectInfo.name}&nbsp;
          <ReferenceBadge value={projectInfo.reference} size="normal" />
          &nbsp;
          <ReferenceBadge
            value={formatClientId(projectInfo.client.id)}
            size="normal"
          />
        </h1>
        <div className="flex gap-2">
          <Button
            label="Filters"
            icon="pi pi-filter"
            onClick={(e) => filterPanelRef.current?.toggle(e)}
            className="p-button-outlined"
          />
        </div>
      </div>

      {/* Chart - Front and Center */}
      <Card className="mb-4">
        <div style={{ height: "500px", width: "100%" }}>
          {chartLoading ? (
            <div
              className="flex justify-content-center align-items-center"
              style={{ height: "100%" }}
            >
              <ProgressSpinner />
            </div>
          ) : chartConfig ? (
            <Chart
              key={`chart-${themeKey}`}
              type="bar"
              data={chartConfig}
              options={chartOptions}
              height="500px"
            />
          ) : (
            <div
              className="flex justify-content-center align-items-center"
              style={{ height: "100%" }}
            >
              <p className="text-center text-600">
                No data available for the selected filters.
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Data Table */}
      <Card title="Hours Summary">
        <DataTable value={visibleTableData} dataKey="id">
          <Column
            style={{ width: "3em" }}
            body={(rowData) => {
              if (rowData.isPersonRow) return null;
              const hasChildren = tableData.some(
                (row) => row.parentActivity === rowData.activity,
              );
              if (!hasChildren) return null;

              const isExpanded = rowData.isExpanded || false;
              return (
                <Button
                  icon={
                    isExpanded ? "pi pi-chevron-down" : "pi pi-chevron-right"
                  }
                  className="p-button-text p-button-plain"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setExpandedRows((prev: any) => ({
                      ...prev,
                      [rowData.activity]: !isExpanded,
                    }));
                  }}
                />
              );
            }}
          />
          <Column
            field="name"
            header="Name"
            body={(rowData) => {
              if (rowData.isPersonRow) {
                return (
                  <span style={{ paddingLeft: "2rem" }}>
                    <i className="pi pi-user mr-2"></i>
                    {rowData.name}
                  </span>
                );
              } else {
                // Activity row - use ActivityBadge
                const activity = projectInfo?.activities.find(
                  (a) => a.id === rowData.activity,
                );
                if (activity) {
                  return (
                    <ActivityBadge
                      value={activity.name}
                      hourType={activity.hour_type}
                    />
                  );
                }
                return rowData.name;
              }
            }}
          />
          <Column field="planned" header="Planned Hours" />
          <Column field="actuals" header="Actual Hours" />
          <Column
            header="Variance"
            body={(rowData) => {
              const variance = rowData.actuals - rowData.planned;
              const color = variance >= 0 ? "green" : "red";
              return (
                <span style={{ color }}>
                  {variance > 0 ? "+" : ""}
                  {variance.toFixed(1)}
                </span>
              );
            }}
          />
        </DataTable>
      </Card>

      {/* Filter OverlayPanel */}
      <OverlayPanel ref={filterPanelRef} style={{ width: "650px" }}>
        <div className="flex flex-column gap-4">
          {/* Activities Section */}
          <div className="field">
            <label htmlFor="activities" className="font-semibold block mb-2">
              Activities:
            </label>
            <MultiSelect
              id="activities"
              value={selectedActivities}
              onChange={(e) => setSelectedActivities(e.value)}
              options={projectInfo.activities.map((a) => ({
                label: a.name,
                value: a.id,
                hourType: a.hour_type,
              }))}
              optionLabel="label"
              optionValue="value"
              display="chip"
              itemTemplate={(option) => (
                <ActivityBadge
                  value={option.label}
                  hourType={option.hourType}
                />
              )}
              placeholder="Select Activities"
              className="w-full"
            />
          </div>

          {/* Week Range Section */}
          <div className="field">
            <label className="font-semibold block mb-3">Time Range:</label>

            {/* Week Pickers */}
            <div className="grid mb-3">
              <div className="col-6">
                <label className="block mb-2 text-sm">From:</label>
                <WeekPicker
                  value={customWeekFrom}
                  year={customYearFrom}
                  onChange={(week, year) => {
                    setCustomWeekFrom(week);
                    setCustomYearFrom(year);
                    setSelectedRange("custom");
                  }}
                />
              </div>
              <div className="col-6">
                <label className="block mb-2 text-sm">To:</label>
                <WeekPicker
                  value={customWeekTo}
                  year={customYearTo}
                  onChange={(week, year) => {
                    setCustomWeekTo(week);
                    setCustomYearTo(year);
                    setSelectedRange("custom");
                  }}
                  minWeek={customWeekFrom}
                  minYear={customYearFrom}
                />
              </div>
            </div>

            {/* Quick Range Buttons */}
            <div className="flex flex-wrap gap-2 mb-3">
              <Button
                label="YTD"
                size="small"
                className="p-button-outlined"
                onClick={() => applyRangePreset("ytd")}
              />
              <Button
                label="Last 26 weeks"
                size="small"
                className="p-button-outlined"
                onClick={() => applyRangePreset("26weeks")}
              />
              <Button
                label="Last 13 weeks"
                size="small"
                className="p-button-outlined"
                onClick={() => applyRangePreset("13weeks")}
              />
              <Button
                label="This Year"
                size="small"
                className="p-button-outlined"
                onClick={() => applyRangePreset("thisyear")}
              />
              <Button
                label="All"
                size="small"
                className="p-button-outlined"
                onClick={() => applyRangePreset("all")}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-content-end gap-2 pt-2 border-top-1 surface-border">
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => filterPanelRef.current?.hide()}
              className="p-button-text"
            />
            <Button label="Apply" icon="pi pi-check" onClick={applyFilters} />
          </div>
        </div>
      </OverlayPanel>
    </div>
  );
};

// Helper function to format ID as CXXXX
const formatClientId = (id: number | string): string => {
  const numericId = typeof id === "string" ? parseInt(id, 10) : id;
  return `C${numericId.toString().padStart(4, "0")}`;
};
